import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  listUsers,
  listUserFeatures,
  setUserFeatures,
  type UserListParams,
  type User,
} from "../api/users";
import { listRoles, assignRole, unassignRole } from "../api/roles";

const PAGE_SIZE = 50;

function FeaturesModal({ user, onClose }: { user: User; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: features, isLoading } = useQuery({
    queryKey: ["user-features", user.user_id],
    queryFn: () => listUserFeatures(user.user_id),
  });
  const [extras, setExtras] = useState<Set<string> | null>(null);

  // Initialise the editable set from the non-baseline enabled features.
  const current =
    extras ??
    new Set((features ?? []).filter((f) => !f.baseline && f.enabled).map((f) => f.permission));

  const save = useMutation({
    mutationFn: () => setUserFeatures(user.user_id, Array.from(current)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-features", user.user_id] });
      onClose();
    },
  });

  function toggle(perm: string) {
    const next = new Set(current);
    if (next.has(perm)) next.delete(perm);
    else next.add(perm);
    setExtras(next);
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-full overflow-auto">
        <h2 className="text-lg font-bold mb-1">Features — {user.username}</h2>
        <p className="text-xs text-gray-500 mb-4">
          Baseline features are always on. Toggle the rest to grant this user extra access.
        </p>
        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {(features ?? []).map((f) => (
              <label
                key={f.permission}
                className={`flex items-center gap-2 text-sm rounded px-2 py-1.5 border ${
                  f.baseline ? "bg-gray-50 border-gray-100" : "border-gray-200"
                }`}
                title={f.baseline ? "Included by default" : ""}
              >
                <input
                  type="checkbox"
                  checked={f.baseline ? true : current.has(f.permission)}
                  disabled={f.baseline}
                  onChange={() => toggle(f.permission)}
                />
                <span>{f.label}</span>
                {f.baseline && <span className="text-[10px] text-gray-400 ml-auto">default</span>}
              </label>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <button
            disabled={save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save features"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function RolesCell({ user }: { user: User }) {
  const qc = useQueryClient();
  const { data: allRoles } = useQuery({ queryKey: ["roles"], queryFn: listRoles });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const assign = useMutation({
    mutationFn: (roleId: number) => assignRole(user.user_id, roleId),
    onSuccess: invalidate,
  });
  const unassign = useMutation({
    mutationFn: (roleId: number) => unassignRole(user.user_id, roleId),
    onSuccess: invalidate,
  });

  const held = new Set(user.roles.map((r) => r.role_id));
  const available = (allRoles ?? []).filter((r) => !held.has(r.role_id));

  return (
    <div className="flex flex-wrap items-center gap-1">
      {user.roles.map((r) => (
        <span
          key={r.role_id}
          className="inline-flex items-center gap-1 bg-brand/10 text-brand rounded px-2 py-0.5 text-xs"
        >
          {r.name}
          <button onClick={() => unassign.mutate(r.role_id)} className="hover:text-red-600" title="Remove role">
            ×
          </button>
        </span>
      ))}
      {available.length > 0 && (
        <select
          className="border border-gray-200 rounded text-xs px-1 py-0.5 text-gray-500"
          value=""
          onChange={(e) => e.target.value && assign.mutate(Number(e.target.value))}
        >
          <option value="">+ role</option>
          {available.map((r) => (
            <option key={r.role_id} value={r.role_id}>
              {r.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

export function UsersPage() {
  const [params, setParams] = useState<UserListParams>({
    page: 1,
    per_page: PAGE_SIZE,
    active_only: false,
  });

  const [featuresFor, setFeaturesFor] = useState<User | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["users", params],
    queryFn: () => listUsers(params),
    placeholderData: keepPreviousData,
  });

  const page = params.page ?? 1;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Users</h1>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={params.active_only ?? false}
            onChange={(e) =>
              setParams((p) => ({ ...p, active_only: e.target.checked, page: 1 }))
            }
          />
          Active only
        </label>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-red-600">{(error as Error).message}</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            {isFetching ? "Loading…" : `${data?.total.toLocaleString() ?? 0} users`}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Username</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Roles</th>
                <th className="px-4 py-2">Features</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-gray-500">
                    No users.
                  </td>
                </tr>
              ) : (
                (data?.data ?? []).map((u) => (
                  <tr key={u.user_id}>
                    <td className="px-4 py-2 font-medium">{u.username}</td>
                    <td className="px-4 py-2">
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{u.email || "—"}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          u.active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {u.active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <RolesCell user={u} />
                    </td>
                    <td className="px-4 py-2">
                      <button onClick={() => setFeaturesFor(u)} className="text-brand hover:underline">
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setParams((p) => ({ ...p, page: page - 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setParams((p) => ({ ...p, page: page + 1 }))}
              className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {featuresFor && <FeaturesModal user={featuresFor} onClose={() => setFeaturesFor(null)} />}
    </div>
  );
}
